/**
 * PrivacySummaryModal Component
 *
 * Full-screen modal showing privacy feature summary.
 * Lists implemented M1-M4 privacy features: encryption, PII scrubbing,
 * data retention, and right to deletion.
 *
 * Design: Native React Native Modal (no third-party), follows CrisisResourcesModal pattern
 * Accessibility: 48px touch targets, semantic roles, screen reader labels
 *
 * @see DD-376 — Privacy Badge & Policy Copy
 */

import {
  Modal,
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';

interface PrivacySummaryModalProps {
  visible: boolean;
  onClose: () => void;
}

interface SectionConfig {
  key: 'encryption' | 'pii_scrubbing' | 'data_retention' | 'deletion';
  icon: 'shield-checkmark' | 'eye-off' | 'time' | 'trash';
  color: string;
}

const SECTIONS: SectionConfig[] = [
  { key: 'encryption', icon: 'shield-checkmark', color: '#10b981' },
  { key: 'pii_scrubbing', icon: 'eye-off', color: '#8b5cf6' },
  { key: 'data_retention', icon: 'time', color: '#3b82f6' },
  { key: 'deletion', icon: 'trash', color: '#ef4444' },
];

export function PrivacySummaryModal({
  visible,
  onClose,
}: PrivacySummaryModalProps): React.JSX.Element {
  const { t } = useTranslation('home');

  const handleClose = (): void => {
    captureEvent('Privacy_Modal_Closed', { screen: 'home' });
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
      testID="privacy-summary-modal"
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>
            {t('privacy_badge.modal.title')}
          </Text>
          <Pressable
            onPress={handleClose}
            style={styles.closeButton}
            testID="privacy-modal-close"
            accessibilityRole="button"
            accessibilityLabel={t('privacy_badge.modal.close')}
          >
            <Ionicons name="close" size={24} color="#6b7280" />
          </Pressable>
        </View>

        {/* Content */}
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {SECTIONS.map((section) => (
            <View key={section.key} style={styles.section}>
              <View style={[styles.iconCircle, { backgroundColor: `${section.color}15` }]}>
                <Ionicons name={section.icon} size={24} color={section.color} />
              </View>
              <View style={styles.sectionText}>
                <Text style={styles.sectionTitle}>
                  {t(`privacy_badge.modal.sections.${section.key}.title`)}
                </Text>
                <Text style={styles.sectionDescription}>
                  {t(`privacy_badge.modal.sections.${section.key}.description`)}
                </Text>
              </View>
            </View>
          ))}

          {/* Footer */}
          <Text style={styles.footer}>
            {t('privacy_badge.modal.footer')}
          </Text>
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' ? { maxWidth: 600, alignSelf: 'center', width: '100%' } : {}),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'ios' ? 16 : 20,
    paddingBottom: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    flex: 1,
  },
  closeButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    gap: 20,
  },
  section: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionText: {
    flex: 1,
    gap: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  footer: {
    fontSize: 12,
    color: '#9ca3af',
    textAlign: 'center',
    marginTop: 12,
    paddingBottom: 20,
  },
});
